/**
 * DetalheVeiculoPage — /veiculos/:seqVeiculo
 *
 * Hero do veiculo + stats (lavagens totais, ultima, gasto total) +
 * historico filtrado por seq_veiculo + acoes (Editar, Trocar placa, Vender).
 *
 * Idioma visual: hero gradient (estilo Apple Wallet/Revolut), cards
 * stat-rounded-2xl, lista de lavagens igual lavagens-page (cards arejados).
 */
import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  CarFront,
  CheckCircle2,
  ChevronRight,
  Droplets,
  KeyRound,
  Pencil,
  Receipt,
  Sparkles,
  Tag,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { TrocarPlacaDialog, VenderVeiculoDialog, type Veiculo } from './veiculos-page';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { useAuth } from '@/lib/auth-context';
import { formatarDataHora, formatBrl } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { useRealtimeInvalidate } from '@/lib/use-realtime-invalidate';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

interface VeiculoFull extends Veiculo {
  cod_padrao_placa: string;
}

interface LavagemDoVeiculo {
  seq_lavagem: number;
  cod_status: string;
  cod_modalidade: string;
  num_placa_lida: string | null;
  qtd_segundos_duracao: number | null;
  val_lavagem: number | null;
  dta_inicio: string;
  dta_fim: string | null;
}

/**
 * Dispara WhatsApp anti-fraude (best-effort, ignora falha).
 * Renderiza template via edge function enviar-mensagem-uazapi.
 */
async function dispararWhatsappAntiFraude(
  codTemplate: 'VEICULO_VENDA_DECLARADA' | 'VEICULO_PLACA_ALTERADA',
  seqCliente: number,
  variaveis: Record<string, string | number>,
): Promise<void> {
  try {
    const { data: cli } = await supabase
      .from('tab_clientes')
      .select('num_whatsapp, nom_cliente')
      .eq('seq_cliente', seqCliente)
      .maybeSingle();
    if (!cli?.num_whatsapp) return;
    await supabase.functions.invoke('enviar-mensagem-uazapi', {
      body: {
        cod_template: codTemplate,
        num_destino: cli.num_whatsapp,
        seq_cliente: seqCliente,
        variaveis: { nom_cliente: cli.nom_cliente, ...variaveis },
      },
    });
  } catch {
    // best-effort
  }
}

/**
 * PlacaVisualBR — render visual da placa brasileira (Mercosul ou antiga).
 * Versao mais robusta da PlacaPreview de adicionar-veiculo-page.
 */
function PlacaVisualBR({ placa, padrao }: { placa: string; padrao: string }) {
  const isMercosul = padrao === 'MERCOSUL';
  const formatado =
    !isMercosul && placa.length === 7 ? `${placa.slice(0, 3)}-${placa.slice(3)}` : placa;
  return (
    <div className="relative mx-auto h-[68px] w-[180px] overflow-hidden rounded-md border-2 border-zinc-800 bg-white text-zinc-900 shadow-md">
      {isMercosul ? (
        <div className="flex h-3 items-center justify-between bg-blue-700 px-1.5 text-[6px] font-bold uppercase text-white">
          <span>BR</span>
          <span className="tracking-widest">MERCOSUL</span>
          <span>BR</span>
        </div>
      ) : (
        <div className="h-3 border-b border-zinc-300" />
      )}
      <div className="flex h-[calc(100%-12px)] items-center justify-center">
        <span className="font-display text-[28px] font-extrabold leading-none tracking-[0.08em]">
          {formatado}
        </span>
      </div>
    </div>
  );
}

export function DetalheVeiculoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { seqVeiculo } = useParams<{ seqVeiculo: string }>();
  const seqVeiculoNum = Number(seqVeiculo);

  const [trocaAberto, setTrocaAberto] = useState<Veiculo | null>(null);
  const [vendaAberto, setVendaAberto] = useState<Veiculo | null>(null);

  // Carrega veiculo (RLS garante que e do cliente atual)
  const veiculo = useQuery({
    queryKey: ['veiculo-detalhe', seqVeiculoNum, user?.id],
    queryFn: async (): Promise<VeiculoFull | null> => {
      if (!seqVeiculoNum) return null;
      const { data, error } = await supabase
        .from('tab_veiculos')
        .select(
          'seq_veiculo, seq_cliente, num_placa, cod_padrao_placa, nom_marca, nom_modelo, des_cor, cod_status, cod_apelido',
        )
        .eq('seq_veiculo', seqVeiculoNum)
        .maybeSingle();
      if (error) throw error;
      return data as VeiculoFull | null;
    },
    enabled: !!user && !!seqVeiculoNum,
  });

  // Lavagens filtradas por veiculo
  const lavagens = useQuery({
    queryKey: ['lavagens-do-veiculo', seqVeiculoNum, user?.id],
    queryFn: async (): Promise<LavagemDoVeiculo[]> => {
      if (!seqVeiculoNum) return [];
      const { data, error } = await supabase
        .from('tab_lavagens')
        .select(
          'seq_lavagem, cod_status, cod_modalidade, num_placa_lida, qtd_segundos_duracao, val_lavagem, dta_inicio, dta_fim',
        )
        .eq('seq_veiculo', seqVeiculoNum)
        .order('dta_inicio', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as LavagemDoVeiculo[];
    },
    enabled: !!user && !!seqVeiculoNum,
  });

  useRealtimeInvalidate('tab_lavagens', ['lavagens-do-veiculo', seqVeiculoNum, user?.id]);
  useRealtimeInvalidate('tab_veiculos', ['veiculo-detalhe', seqVeiculoNum, user?.id]);

  const trocarPlaca = useMutation({
    mutationFn: async (vars: {
      seq: number;
      placaAntiga: string;
      nova: string;
      motivo: string;
      des: string;
      seqCliente: number;
    }) => {
      const { error } = await sb.rpc('fn_trocar_placa_veiculo', {
        p_seq_veiculo: vars.seq,
        p_nova_placa: vars.nova,
        p_cod_motivo: vars.motivo,
        p_des_motivo: vars.des || null,
      });
      if (error) throw error;
      await dispararWhatsappAntiFraude('VEICULO_PLACA_ALTERADA', vars.seqCliente, {
        num_placa_anterior: vars.placaAntiga,
        num_placa_nova: vars.nova,
      });
    },
    onSuccess: () => {
      toast.success('Placa atualizada', {
        description: 'Enviamos um WhatsApp de confirmacao.',
      });
      void queryClient.invalidateQueries({ queryKey: ['veiculo-detalhe'] });
      void queryClient.invalidateQueries({ queryKey: ['cliente-veiculos'] });
      setTrocaAberto(null);
    },
    onError: (e) => toast.error('Falha ao trocar placa', { description: traduzirErroSupabase(e) }),
  });

  const venderVeiculo = useMutation({
    mutationFn: async (vars: {
      seq: number;
      motivo: string;
      placa: string;
      seqCliente: number;
    }) => {
      const { error } = await sb.rpc('fn_marcar_veiculo_vendido', {
        p_seq_veiculo: vars.seq,
        p_des_motivo: vars.motivo || null,
      });
      if (error) throw error;
      await dispararWhatsappAntiFraude('VEICULO_VENDA_DECLARADA', vars.seqCliente, {
        num_placa: vars.placa,
        cod_motivo: 'VENDA',
        dias_bloqueio: 60,
        dta_liberacao: new Date(Date.now() + 60 * 86_400_000).toLocaleDateString('pt-BR'),
      });
    },
    onSuccess: () => {
      toast.success('Veiculo removido', {
        description: 'Enviamos um WhatsApp de confirmacao.',
      });
      void queryClient.invalidateQueries({ queryKey: ['cliente-veiculos'] });
      setVendaAberto(null);
      navigate('/veiculos');
    },
    onError: (e) =>
      toast.error('Falha ao remover veiculo', { description: traduzirErroSupabase(e) }),
  });

  // ---------------------------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------------------------
  if (veiculo.isLoading) {
    return (
      <div className="space-y-5 p-4 pb-24">
        <Skeleton className="h-12 w-32 rounded-xl" />
        <Skeleton className="h-56 rounded-3xl" />
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
          <Skeleton className="h-24 rounded-2xl" />
        </div>
        <Skeleton className="h-40 rounded-2xl" />
      </div>
    );
  }

  if (!veiculo.data) {
    return (
      <div className="space-y-4 p-6 pb-24 text-center">
        <p className="text-muted-foreground">Veiculo nao encontrado ou sem acesso.</p>
        <Button variant="outline" onClick={() => navigate('/veiculos')} className="rounded-xl">
          Voltar pra meus veiculos
        </Button>
      </div>
    );
  }

  const v = veiculo.data;
  const ativo = v.cod_status === 'ATIVO';
  const totalLavagens = lavagens.data?.length ?? 0;
  const lavagensConcluidas = lavagens.data?.filter((l) => l.cod_status === 'CONCLUIDA') ?? [];
  const ultimaLavagem = lavagensConcluidas[0];
  const gastoTotal = lavagensConcluidas.reduce((acc, l) => acc + Number(l.val_lavagem ?? 0), 0);

  const nomeBonito = v.cod_apelido ?? `${v.nom_marca ?? ''} ${v.nom_modelo ?? ''}`.trim();

  return (
    <div className="space-y-5 p-4 pb-24">
      {/* Header com back */}
      <div className="octa-fade-in-up flex items-start gap-3 pt-1">
        <button
          type="button"
          onClick={() => navigate('/veiculos')}
          aria-label="Voltar"
          className="octa-tap text-muted-foreground hover:text-foreground border-border/40 bg-card/60 hover:bg-accent/60 mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border backdrop-blur-sm transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 space-y-1">
          <p className="text-overline text-muted-foreground uppercase">Sua frota</p>
          <h1 className="text-display-md font-extrabold tracking-tight">
            {nomeBonito || 'Veiculo'}
          </h1>
          {v.des_cor && <p className="text-muted-foreground text-sm">{v.des_cor}</p>}
        </div>
      </div>

      {/* Hero com placa visual e status — estilo Apple Wallet */}
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card
          variant="gradient"
          className="relative overflow-hidden rounded-3xl border-0 text-white"
        >
          <CardContent className="relative z-10 space-y-5 p-6">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/15 blur-3xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-white/10 blur-2xl"
            />
            <div className="relative flex items-start justify-between">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 ring-2 ring-white/40 backdrop-blur">
                <CarFront className="h-7 w-7" />
              </div>
              <StatusBadge variant={ativo ? 'success' : 'neutral'} dot>
                {v.cod_status.replace('_', ' ')}
              </StatusBadge>
            </div>

            <div className="relative pt-2">
              <PlacaVisualBR placa={v.num_placa} padrao={v.cod_padrao_placa} />
            </div>

            <div className="relative">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/85">
                Modelo
              </p>
              <p className="mt-0.5 text-base font-semibold">
                {v.nom_marca && v.nom_modelo
                  ? `${v.nom_marca} ${v.nom_modelo}`
                  : 'Sem modelo cadastrado'}
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Stats em 3 colunas */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          icone={Sparkles}
          label="Lavagens"
          valor={totalLavagens.toString()}
          sub="historico total"
          cor="from-sky-500 to-blue-700"
          loading={lavagens.isLoading}
        />
        <StatCard
          icone={Droplets}
          label="Ultima"
          valor={ultimaLavagem ? formatarDataHora(ultimaLavagem.dta_inicio).split(' ')[0]! : '—'}
          sub={
            ultimaLavagem
              ? (formatarDataHora(ultimaLavagem.dta_inicio).split(' ')[1] ?? '')
              : 'nenhuma'
          }
          cor="from-emerald-500 to-teal-600"
          loading={lavagens.isLoading}
        />
        <StatCard
          icone={Receipt}
          label="Gasto"
          valor={formatBrl(gastoTotal).replace('R$', 'R$').replace(/\s/g, ' ')}
          sub="acumulado"
          cor="from-violet-500 to-purple-700"
          loading={lavagens.isLoading}
        />
      </div>

      {/* Acoes */}
      {ativo && (
        <div className="grid grid-cols-3 gap-2">
          <Link to={`/veiculos/${v.seq_veiculo}/editar`}>
            <Button variant="outline" className="h-12 w-full rounded-xl">
              <Pencil className="h-4 w-4" />
              Editar
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => setTrocaAberto(v)}
            className="h-12 w-full rounded-xl"
          >
            <KeyRound className="h-4 w-4" />
            Placa
          </Button>
          <Button
            variant="outline"
            onClick={() => setVendaAberto(v)}
            className="h-12 w-full rounded-xl border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:border-rose-900/40 dark:hover:bg-rose-950/30"
          >
            <Tag className="h-4 w-4" />
            Vendi
          </Button>
        </div>
      )}

      {/* Historico de lavagens do veiculo */}
      <div>
        <h2 className="text-muted-foreground mb-3 text-[11px] font-semibold uppercase tracking-[0.18em]">
          Historico deste veiculo
        </h2>
        {lavagens.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
          </div>
        ) : totalLavagens === 0 ? (
          <EmptyState
            variant="subtle"
            icon={Droplets}
            title="Ainda sem lavagens"
            description="Quando este veiculo passar por uma lavagem, aparece aqui."
          />
        ) : (
          <div className="space-y-3">
            {lavagens.data?.map((l) => {
              const isDone = l.cod_status === 'CONCLUIDA';
              const isRunning = l.cod_status === 'EM_ANDAMENTO' || l.cod_status === 'INICIADA';
              const variant: 'success' | 'info' | 'danger' | 'neutral' = isDone
                ? 'success'
                : isRunning
                  ? 'info'
                  : l.cod_status === 'CANCELADA' || l.cod_status === 'FALHA'
                    ? 'danger'
                    : 'neutral';
              return (
                <Link
                  key={l.seq_lavagem}
                  to={`/lavagem/${l.seq_lavagem}`}
                  className="octa-card-tap block"
                >
                  <Card variant="elevated" className="overflow-hidden">
                    <CardContent className="flex items-center gap-3 p-4">
                      <div
                        className={`shadow-octa-sm flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                          isDone
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'
                            : isRunning
                              ? 'bg-octa-gradient text-white'
                              : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {isDone ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <Droplets className={`h-5 w-5 ${isRunning ? 'animate-pulse' : ''}`} />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">{formatarDataHora(l.dta_inicio)}</p>
                          <StatusBadge variant={variant} dot pulse={isRunning}>
                            {l.cod_status}
                          </StatusBadge>
                        </div>
                        <p className="text-muted-foreground mt-0.5 text-[11px]">
                          {l.cod_modalidade}
                          {l.val_lavagem != null && ` · ${formatBrl(Number(l.val_lavagem))}`}
                          {l.qtd_segundos_duracao != null &&
                            ` · ${Math.round(l.qtd_segundos_duracao / 60)}min`}
                        </p>
                      </div>
                      <ChevronRight className="text-muted-foreground/60 h-4 w-4 shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Modais reaproveitados de veiculos-page */}
      {trocaAberto && (
        <TrocarPlacaDialog
          veiculo={trocaAberto}
          onSubmit={(nova, motivo, des) =>
            trocarPlaca.mutate({
              seq: trocaAberto.seq_veiculo,
              placaAntiga: trocaAberto.num_placa,
              nova,
              motivo,
              des,
              seqCliente: trocaAberto.seq_cliente,
            })
          }
          onClose={() => setTrocaAberto(null)}
          carregando={trocarPlaca.isPending}
        />
      )}
      {vendaAberto && (
        <VenderVeiculoDialog
          veiculo={vendaAberto}
          onSubmit={(motivo) =>
            venderVeiculo.mutate({
              seq: vendaAberto.seq_veiculo,
              motivo,
              placa: vendaAberto.num_placa,
              seqCliente: vendaAberto.seq_cliente,
            })
          }
          onClose={() => setVendaAberto(null)}
          carregando={venderVeiculo.isPending}
        />
      )}
    </div>
  );
}

// ============================================================================
// StatCard auxiliar
// ============================================================================
function StatCard({
  icone: Icone,
  label,
  valor,
  sub,
  cor,
  loading,
}: {
  icone: typeof Sparkles;
  label: string;
  valor: string;
  sub: string;
  cor: string;
  loading: boolean;
}) {
  if (loading) {
    return <Skeleton className="h-24 rounded-2xl" />;
  }
  return (
    <Card variant="elevated" className="octa-card-tap rounded-2xl">
      <CardContent className="p-3.5">
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
            {label}
          </p>
          <div
            className={`flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br ${cor} text-white`}
          >
            <Icone className="h-3 w-3" />
          </div>
        </div>
        <p className="text-octa-gradient mt-1.5 text-xl font-bold tabular-nums leading-tight">
          {valor}
        </p>
        <p className="text-muted-foreground text-[10px]">{sub}</p>
      </CardContent>
    </Card>
  );
}
